/**
 * WorkforceBenchPage — /workforce
 * Demo: /manage-team (members table with search, status filters and a header action) and /teams (member cards).
 * Built on ListPage. Hire goes to the marketplace workforce listings: employees are acquired there.
 */
import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, Plus, Users } from 'lucide-react';
import { Badge, Button, Callout, EmptyState } from '../../components/ui';
import { ListPage } from '../../components/page/index.js';
import { buttonStyles } from '../../components/tailgrids/core/button.js';
import { PAGE_HELP } from '../../help/index.js';
import { useEmployees, useWorkforceAssignments, useWorkforceSummary } from '../../hooks/workforce';
import {
  applyAssignmentsToView,
  benchSortValue,
  computeFleetSummary,
  matchesBenchFilter,
  usePref,
  type BenchFilter,
  type EmployeeView,
} from './shared';
import { benchStatCells } from './bench/BenchFleetStrip.js';
import { BenchRosterCards } from './bench/BenchRosterCards.js';
import { BenchRosterTable } from './bench/BenchRosterTable.js';
import { BenchToolbar, type BenchView } from './bench/BenchToolbar.js';

const FILTER_KEYS: BenchFilter[] = ['all', 'attention', 'assigned', 'unassigned', 'paused', 'needs_setup'];
const MARKETPLACE_WORKFORCE_PATH = '/marketplace?type=workforce';

function matchesSearch(emp: EmployeeView, q: string): boolean {
  if (!q) return true;
  const needle = q.toLowerCase();
  return (
    emp.name.toLowerCase().includes(needle) ||
    emp.role.toLowerCase().includes(needle) ||
    emp.skills.some((s) => s.toLowerCase().includes(needle)) ||
    emp.assignments.some((a) => a.projectName.toLowerCase().includes(needle))
  );
}

export const WorkforceBenchPage: React.FC = () => {
  const navigate = useNavigate();
  const { employees, isLoading, error, refetch } = useEmployees();
  const { byEmployee } = useWorkforceAssignments();
  const { data: fleetSummaryData } = useWorkforceSummary();
  const [filter, setFilter] = useState<BenchFilter>('all');
  const [search, setSearch] = useState('');
  const [view, setView] = usePref<BenchView>('benchView', 'table');

  // Merge the real assignment feed into each employee (recomputes Load).
  const merged = useMemo(
    () => employees.map((emp) => applyAssignmentsToView(emp, byEmployee.get(emp.id) ?? [])),
    [employees, byEmployee],
  );

  const summary = useMemo(() => computeFleetSummary(merged), [merged]);

  const counts = useMemo(() => {
    const c = {} as Record<BenchFilter, number>;
    for (const key of FILTER_KEYS) c[key] = merged.filter((e) => matchesBenchFilter(e, key)).length;
    return c;
  }, [merged]);

  const visible = useMemo(
    () =>
      merged
        .filter((e) => matchesBenchFilter(e, filter) && matchesSearch(e, search))
        .sort((a, b) => benchSortValue(b) - benchSortValue(a)),
    [merged, filter, search],
  );

  const openProfile = (id: string) => navigate(`/workforce/${id}`);

  const hireLink = (
    <Link
      to={MARKETPLACE_WORKFORCE_PATH}
      className={buttonStyles({ variant: 'primary', appearance: 'fill', size: 'sm' })}
    >
      <Plus size={14} aria-hidden="true" />
      Hire
    </Link>
  );

  const help = PAGE_HELP['workforce'];
  const empty =
    merged.length === 0 ? (
      <EmptyState
        icon={Users}
        title={help?.emptyState?.title ?? 'No employees yet'}
        description={
          help?.emptyState?.description ??
          'Hire your first employee to staff projects. Employees belong to the organization and can be assigned across projects.'
        }
        learnItems={help?.emptyState?.learnItems}
        action={hireLink}
      />
    ) : (
      <EmptyState
        icon={Users}
        title="No employees match this filter"
        description="Try another status or clear the search."
        action={
          <Button
            variant="secondary"
            onClick={() => {
              setFilter('all');
              setSearch('');
            }}
          >
            Show all
          </Button>
        }
      />
    );

  const showCards = view === 'cards' && visible.length > 0;

  return (
    <ListPage
      title="Workforce"
      subtitle="Every employee the organization owns. Lifecycle, assignments, current focus and attention are tracked separately across projects."
      breadcrumbs={[{ label: 'Workforce' }]}
      helpContent={help}
      actions={
        <>
          <Badge variant="purple" dot>
            Org-wide
          </Badge>
          {hireLink}
        </>
      }
      stats={!isLoading && merged.length > 0 ? benchStatCells(summary, fleetSummaryData?.loadBuckets) : undefined}
      alert={
        error ? (
          <Callout
            tone="danger"
            icon={AlertTriangle}
            title="Failed to load the workforce"
            actions={
              <Button size="sm" variant="secondary" onClick={() => refetch()}>
                Retry
              </Button>
            }
          >
            Something went wrong fetching the employees. Check your connection and try again.
          </Callout>
        ) : undefined
      }
      toolbar={
        merged.length > 0 || isLoading ? (
          <BenchToolbar
            filter={filter}
            setFilter={setFilter}
            counts={counts}
            search={search}
            setSearch={setSearch}
            view={view}
            setView={setView}
          />
        ) : undefined
      }
      variant={showCards ? 'grid' : 'table'}
      footer={
        merged.length > 0 ? (
          <p className="text-sm text-muted">
            Showing {visible.length} of {merged.length}. Attention comes first, then assigned work.
          </p>
        ) : undefined
      }
    >
      {error ? null : showCards ? (
        <BenchRosterCards employees={visible} />
      ) : (
        <BenchRosterTable employees={visible} onOpen={openProfile} loading={isLoading} empty={empty} />
      )}
    </ListPage>
  );
};
